import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doFetch, s3upload } from '../redux/api/actions';
import { GET_TOURS } from '../redux/api/constants';
import './MapAddTrackModal.css';

const MapAddTrackModal = ({ isOpen, onClose, latitude, longitude, onSuccess }) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(state => state.Auth.accessToken);
  const tours = useSelector(state => state.API.tours);

  const [selectedTourId, setSelectedTourId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [textContent, setTextContent] = useState('');
  const [sequenceOrder, setSequenceOrder] = useState(1);
  const [audioFile, setAudioFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // New tour creation state
  const [creatingNewTour, setCreatingNewTour] = useState(false);
  const [newTourTitle, setNewTourTitle] = useState('');
  const [newTourDescription, setNewTourDescription] = useState('');
  const [newTourCategory, setNewTourCategory] = useState('General');

  // Fetch user's tours on mount
  useEffect(() => {
    if (isOpen && accessToken) {
      dispatch(doFetch(GET_TOURS, 'tourguide/tours', accessToken));
    }
  }, [isOpen, accessToken, dispatch]);

  // Extract duration from audio file using Web Audio API
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAudioFile(file);
    setError(null);

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const durationSeconds = Math.floor(audioBuffer.duration);
      setDuration(durationSeconds);
    } catch (err) {
      console.error('Error reading audio file:', err);
      setError('Could not read audio file. Please try a different file.');
    }
  };

  const handleCreateNewTour = async () => {
    if (!newTourTitle.trim()) {
      setError('Please enter a tour title');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/tourguide/tours`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: newTourTitle.trim(),
            description: newTourDescription.trim(),
            category: newTourCategory,
            privacy_level: 'PUBLIC'
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create tour');
      }

      const newTour = await response.json();

      // Refresh tours list
      dispatch(doFetch(GET_TOURS, 'tourguide/tours', accessToken));

      // Select the new tour
      setSelectedTourId(newTour.id);
      setCreatingNewTour(false);
      setNewTourTitle('');
      setNewTourDescription('');
      setNewTourCategory('General');
      setUploading(false);
    } catch (err) {
      console.error('Error creating tour:', err);
      setError(err.message || 'Failed to create tour');
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedTourId || selectedTourId === 'new') {
      setError('Please select a tour or create a new one');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    if (!audioFile && !textContent.trim()) {
      setError('Please provide either audio or text content');
      return;
    }

    setUploading(true);

    try {
      let s3Key = null;

      // Step 1: Upload audio file if provided
      if (audioFile) {
        const presignedResponse = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/tourguide/tours/${selectedTourId}/tracks/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              filename: audioFile.name
            })
          }
        );

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const presignedData = await presignedResponse.json();

        // Upload file to S3
        dispatch(s3upload([audioFile], [presignedData.presigned_url]));

        // Wait a moment for upload to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        s3Key = presignedData.s3_key;
      }

      // Step 2: Create track record
      const trackPayload = {
        title: title.trim(),
        description: description.trim(),
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        sequence_order: parseInt(sequenceOrder)
      };

      if (audioFile) {
        trackPayload.duration_seconds = duration;
        trackPayload.filename = s3Key;
      }

      if (textContent.trim()) {
        trackPayload.text_content = textContent.trim();
      }

      const createResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/tourguide/tours/${selectedTourId}/tracks`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(trackPayload)
        }
      );

      if (!createResponse.ok) {
        throw new Error('Failed to create tour stop');
      }

      const trackData = await createResponse.json();

      // Success!
      setUploading(false);
      onSuccess(trackData);
      handleClose();
    } catch (err) {
      console.error('Error uploading track:', err);
      setError(err.message || 'Failed to upload track. Please try again.');
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedTourId('');
    setTitle('');
    setDescription('');
    setTextContent('');
    setSequenceOrder(1);
    setAudioFile(null);
    setDuration(0);
    setError(null);
    setUploading(false);
    setCreatingNewTour(false);
    setNewTourTitle('');
    setNewTourDescription('');
    setNewTourCategory('General');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content map-add-track-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Tour Stop</h2>
          <button className="modal-close" onClick={handleClose} disabled={uploading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Tour *</label>
            <select
              value={selectedTourId}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'new') {
                  setCreatingNewTour(true);
                  setSelectedTourId('');
                } else {
                  setSelectedTourId(value);
                  setCreatingNewTour(false);
                }
              }}
              required
              disabled={uploading}
            >
              <option value="">Select a tour...</option>
              {tours.map(tour => (
                <option key={tour.id} value={tour.id}>
                  {tour.title}
                </option>
              ))}
              <option value="new">+ Create New Tour</option>
            </select>
          </div>

          {/* New Tour Creation Form */}
          {creatingNewTour && (
            <div className="new-tour-form">
              <div className="form-group">
                <label>New Tour Title *</label>
                <input
                  type="text"
                  value={newTourTitle}
                  onChange={(e) => setNewTourTitle(e.target.value)}
                  placeholder="Tour title"
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>New Tour Description</label>
                <textarea
                  value={newTourDescription}
                  onChange={(e) => setNewTourDescription(e.target.value)}
                  placeholder="Tour description (optional)"
                  rows="2"
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newTourCategory}
                  onChange={(e) => setNewTourCategory(e.target.value)}
                  disabled={uploading}
                >
                  <option value="General">General</option>
                  <option value="Ghost Tour">Ghost Tour</option>
                  <option value="Historical">Historical</option>
                  <option value="Pop Culture">Pop Culture</option>
                  <option value="Family History">Family History</option>
                  <option value="Food & Drink">Food & Drink</option>
                  <option value="Architecture">Architecture</option>
                  <option value="Nature & Parks">Nature & Parks</option>
                  <option value="Art & Culture">Art & Culture</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleCreateNewTour}
                disabled={uploading || !newTourTitle.trim()}
                className="btn-primary"
                style={{ marginBottom: '16px' }}
              >
                Create Tour
              </button>
            </div>
          )}

          <div className="form-group">
            <label>Location</label>
            <div className="location-display">
              <span>📍 {latitude.toFixed(6)}, {longitude.toFixed(6)}</span>
            </div>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Stop title"
              required
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>Text Content</label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Type what you want to say at this stop..."
              rows="4"
              disabled={uploading}
            />
            <small>Provide either text content or audio (or both)</small>
          </div>

          <div className="form-group">
            <label>Audio File (Optional)</label>
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {duration > 0 && (
              <small className="duration-info">Duration: {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}</small>
            )}
          </div>

          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any additional notes (optional)"
              rows="2"
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>Sequence Order</label>
            <input
              type="number"
              value={sequenceOrder}
              onChange={(e) => setSequenceOrder(e.target.value)}
              min="1"
              disabled={uploading}
            />
            <small>Order of this track in the tour (1 = first)</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={handleClose} disabled={uploading} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? 'Saving...' : 'Add Tour Stop'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MapAddTrackModal;
